"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="uppercase font-default text-[12px] tracking-[0.1em] text-foreground/80 hover:text-primary transition-colors relative after:absolute after:bottom-[-4px] after:left-1/2 after:w-0 after:h-[1px] after:bg-primary after:transition-all hover:after:w-full hover:after:left-0">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="uppercase font-default text-lg tracking-widest text-foreground hover:text-primary transition-colors">
          {item.label}
        </Link>)}
      <div className="pt-8 border-t border-border mt-4" data-index="cta">
        <Link to="#secure-your-spot">
          <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground uppercase tracking-widest text-xs font-default py-6 rounded-md">
            Register Now
          </Button>
        </Link>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="265036" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-lg border-b border-border/50 shadow-md" : ""}`}>
      <div className="container mx-auto px-4 h-24 flex items-center justify-between">
        {}
        <div className="md:hidden flex-1 flex justify-start">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/20">
                <Menu className="size-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-background border-r border-border w-[300px] sm:w-[400px]">
              <div className="flex justify-start mb-8 mt-4">
                <WvcLogo className="h-8 w-auto text-primary" />
              </div>
              <MenuProvider menu_id="45">
                <MobileMenuConsumer />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-start">
          <MenuProvider menu_id="45">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex-shrink-0 flex justify-center">
          <Link to="/HomePage">
            <WvcLogo className="h-10 w-auto transition-transform duration-300 hover:scale-105 text-primary" />
          </Link>
        </div>

        {}
        <div className="flex-1 flex justify-end">
          <Link to="#secure-your-spot">
            <Button variant="outline" className="hidden md:inline-flex border-primary text-primary hover:bg-gradient-to-r hover:from-primary hover:to-chart-4 hover:text-primary-foreground uppercase tracking-widest text-xs font-default px-8 py-5 transition-all duration-300 rounded-md shadow-sm">
              Register Now
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}